import _ from 'lodash'
import { useState, useEffect } from 'react'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import SearchAndFilter from '../SearchAndFilter'
import { AIIcon } from '@/components/icons/AIIcon'
import { Checkbox } from '@/components/ui/checkbox'
import { Textarea } from '@/components/ui/textarea'
import { LoaderCircle, Loader2 } from 'lucide-react'
import useImageMirroring from '../useImageMirroring'
import { Alert, AlertTitle } from '@/components/ui/alert'
import WizardSteps from '@/components/common/wizard/WizardSteps'
import { Tabs, TabsList, TabsContent, TabsTrigger } from '@/components/ui/tabs'
import { useWizard } from '@/components/mediaBuying/adCreatives/AdCreativeWizard'
import Library from '@/components/mediaBuying/adCreatives/screens/aiAssetGeneration/ImageLibrary'
import { useImageGeneration } from '@/components/mediaBuying/adCreatives/screens/aiAssetGeneration/useImageGeneration'
import { UploadIcon } from '@/components/icons'
import { useQuery } from '@apollo/client'
import { useMediaBuyingContext } from '@/context/media-buying-context'
import { GET_MEDIA } from '@/graphql/ads/media-library.queries'
import { GET_AD_ASSETS } from '@/graphql/ads/ad-assets-queries'
import MediaLibraryTabContent from './MediaLibraryTabContent'

const Footer = ({ handleNextNavigation, handleBackNavigation, disabled }) => {
    return (
        <div className="tw:w-full">
            <div className="tw:flex tw:justify-end tw:gap-4 tw:w-full">
                <Button variant="secondary" className="tw:w-[156px] tw:!text-sm" onClick={handleBackNavigation}>
                    Back
                </Button>
                <Button onClick={handleNextNavigation} disabled={disabled} className="tw:w-[156px] tw:!text-sm">
                    Next
                </Button>
            </div>
        </div>
    )
}

const AiImageToImage = () => {
    const [showUpload, setShowUpload] = useState(false)
    const [searchValue, setSearchValue] = useState('')
    const [filterFormats, setFilterFormats] = useState([])
    const [mediaSearchQuery, setMediaSearchQuery] = useState('')
    const [adAssets, setAdAssets] = useState([])
    const [isUploadMode, setIsUploadMode] = useState(false)
    const [activeTab, setActiveTab] = useState('media-library')
    const [isGenerating, setIsGenerating] = useState(false)

    const {
        selectedFormats,
        prompt,
        setPrompt,
        loading,
        generatedImages,
        selectedImages,
        setSelectedImages,
        showDeleteConfirmationModal,
        handleFormatSelection,
        handleGenerate,
        handleDelete,
        handleConfirmDelete,
        handleSelect,
        setShowDeleteConfirmationModal,
    } = useImageGeneration('imageToImage')

    const { isMirroring, mirrorImageAndNavigateToNextScreen } = useImageMirroring({
        images: generatedImages,
        selectedImages,
    })

    // Helper function to get images filtered by source
    const getSelectedImagesBySource = (sources) => {
        if (!Array.isArray(sources)) sources = [sources]

        return selectedImages.filter((item) => {
            // Handle both string IDs (backward compat) and objects with source
            if (typeof item === 'string') {
                // String IDs don't have source, so we can't filter them
                return false
            }
            return sources.includes(item.source)
        })
    }

    // Check if there are selected images from the active tab
    const hasSelectedImagesForActiveTab = () => {
        if (activeTab === 'ai-library') {
            const aiImages = getSelectedImagesBySource('ai')
            return aiImages.length > 0
        } else if (activeTab === 'media-library') {
            const mediaImages = getSelectedImagesBySource(['upload', 'meta'])
            // Also check for string IDs (backward compat) when in media-library tab
            const stringIds = selectedImages.filter((item) => typeof item === 'string')
            return mediaImages.length > 0 || stringIds.length > 0
        }
        return false
    }

    const {
        goToStep,
        registerCustomNavigation,
        clearCustomNavigation,
        campaignFormat,
        dataForStepper,
        campaignId,
        id: adId,
    } = useWizard()
    const { connectedPublishers } = useMediaBuyingContext()

    const publisherId = connectedPublishers ? Object.keys(connectedPublishers)[0] : null
    const adAccountId = publisherId ? connectedPublishers[publisherId].ad_account : null

    const { data: mediaData, loading: mediaLoading } = useQuery(GET_MEDIA, {
        variables: { adAccountId, publisherId, campaignId },
        skip: !connectedPublishers || !campaignId,
    })

    // Fetch ad assets data with uploadForAi source filter
    const { data: adAssetsData, refetch: refetchAdAssets } = useQuery(GET_AD_ASSETS, {
        variables: { campaignId, adId, hasMetaId: false, source: 'uploadForAi' },
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
        onCompleted: (data) => setAdAssets(data.AdAsset?.adAssets || []),
        skip: !campaignId || !adId,
    })

    // Update ad assets when the query data changes
    useEffect(() => {
        if (adAssetsData?.AdAsset?.adAssets) {
            setAdAssets(adAssetsData.AdAsset.adAssets)
        }
    }, [adAssetsData])

    const handleBackNavigation = () => goToStep('uploadAssets')

    useEffect(() => {
        const customNav = (
            <Footer
                handleBackNavigation={handleBackNavigation}
                handleNextNavigation={mirrorImageAndNavigateToNextScreen}
                disabled={isMirroring || selectedImages.length === 0}
            />
        )
        registerCustomNavigation(customNav)

        return () => clearCustomNavigation()
    }, [selectedImages, isMirroring])

    const handleGenerateClick = async () => {
        // Filter selected images based on active tab and take only the first one
        let imageToGenerate = null

        if (activeTab === 'ai-library') {
            const aiImages = getSelectedImagesBySource('ai')
            imageToGenerate = aiImages.length > 0 ? aiImages[0] : null
        } else if (activeTab === 'media-library') {
            const mediaImages = getSelectedImagesBySource(['upload', 'meta'])
            if (mediaImages.length > 0) {
                imageToGenerate = mediaImages[0]
            } else {
                // Check for string IDs for backward compatibility
                const stringIds = selectedImages.filter((item) => typeof item === 'string')
                imageToGenerate = stringIds.length > 0 ? stringIds[0] : null
            }
        }

        // Generate with only the first selected image from the active tab
        if (imageToGenerate) {
            setIsGenerating(true)
            setSelectedImages([imageToGenerate])
            setPrompt('')
            await handleGenerate()
            setSelectedImages([])
            goToStep('aiAssetLibrary')
        }
    }

    const handleUploadComplete = async () => {
        await refetchAdAssets()
        setIsUploadMode(false)
    }

    const onUploadButtonClick = () => {
        setActiveTab('media-library')
        setShowUpload(true)
        setIsUploadMode(true)
    }

    return (
        <div className="tw:flex tw:h-full">
            <WizardSteps adCreative={dataForStepper} currentStep="aiImageToImage" />

            <div className="flex-grow-1  overflow-auto">
                <div>
                    <div className="tw:px-6 tw:py-4">
                        <span className="tw:text-base tw:font-semibold">Generate variations from existing image</span>

                        <div className="tw:mt-4">
                            <span className="tw:text-sm tw:font-semibold">Aspect Ratio</span>
                            <div className="tw:flex tw:space-x-8 tw:mt-4">
                                {['square', 'vertical', 'horizontal'].map((ratio) => (
                                    <div key={ratio} className="tw:flex tw:items-center tw:gap-2">
                                        <Checkbox
                                            id={ratio}
                                            checked={selectedFormats[ratio]}
                                            onCheckedChange={() => handleFormatSelection(ratio)}
                                        />
                                        <Label htmlFor={ratio} className="tw:text-white tw:text-sm capitalize">
                                            {_.startCase(ratio)} (
                                            {ratio === 'square' ? '1:1' : ratio === 'vertical' ? '9:16' : '1.91:1'})
                                        </Label>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="tw:mt-6 tw:max-w-[500px] tw:flex tw:flex-col tw:gap-4">
                            <div className="tw:flex tw:flex-col tw:gap-2">
                                <div className="tw:flex tw:gap-2">
                                    <span className="tw:text-sm tw:font-semibold">Prompt</span>
                                    <span className="tw:text-sm tw:font-semibold tw:text-[#4E6986]">Optional</span>
                                </div>
                                <span className="tw:text-sm">
                                    Describe the variations you want to generate from the selected image.
                                </span>
                            </div>
                            <Textarea
                                value={prompt}
                                onChange={(e) => setPrompt(e.target.value)}
                                placeholder="Type image variation description"
                                className="tw:min-h-24 tw:mt-4 tw:bg-transparent tw:border-gray-600 tw:text-white tw:placeholder:text-gray-400 tw:focus:border-blue-400"
                            />
                            <span className="tw:text-sm tw:font-semibold tw:mt-2">
                                Select an image to generate variations from
                            </span>
                        </div>

                        {isMirroring && (
                            <Alert className="tw:text-left tw:mt-6">
                                <Loader2 className="tw:h-4 tw:w-4 tw:animate-spin" />
                                <AlertTitle className="tw:text-left">
                                    Please wait while we process your images.
                                </AlertTitle>
                            </Alert>
                        )}
                    </div>

                    <Tabs value={activeTab} onValueChange={setActiveTab}>
                        <div className="tw:border-b tw:w-full tw:h-11">
                            <div className="tw:flex tw:justify-between tw:px-6">
                                <TabsList className="tw:pb-0  tw:mt-0.5">
                                    <TabsTrigger
                                        className="tw:border-b-[4px] tw:data-[state=active]:bg-gray-500 tw:h-11 tw:font-semibold tw:dark:hover:border-blue-300 tw:transition-all tw:duration-300"
                                        value="media-library"
                                    >
                                        Medial Library
                                    </TabsTrigger>
                                    <TabsTrigger
                                        className="tw:border-b-[4px] tw:data-[state=active]:bg-gray-500 tw:h-11 tw:font-semibold tw:dark:hover:border-blue-300 tw:transition-all tw:duration-300"
                                        value="ai-library"
                                    >
                                        AI Generated
                                    </TabsTrigger>
                                </TabsList>
                                {!isUploadMode && (
                                    <Button variant="secondary" className="tw:gap-1" onClick={onUploadButtonClick}>
                                        <UploadIcon />
                                        Upload Image
                                    </Button>
                                )}
                            </div>
                        </div>
                        <TabsContent value="media-library" className="tw:animate-fadeIn">
                            <div className="tw:animate-slideInLeft">
                                <MediaLibraryTabContent
                                    campaignId={campaignId}
                                    adId={adId}
                                    showUpload={showUpload}
                                    setShowUpload={setShowUpload}
                                    campaignFormat={campaignFormat}
                                    selectedImages={selectedImages}
                                    handleSelect={handleSelect}
                                    mediaData={mediaData}
                                    adAssets={adAssets}
                                    mediaLoading={mediaLoading}
                                    mediaSearchQuery={mediaSearchQuery}
                                    onSearchChange={setMediaSearchQuery}
                                    onUploadModeChange={setIsUploadMode}
                                    onUploadComplete={handleUploadComplete}
                                />
                            </div>
                        </TabsContent>
                        <TabsContent value="ai-library" className="tw:animate-fadeIn">
                            <div className="tw:px-6 tw:py-6 tw:pb-0  tw:animate-slideInRight">
                                <div className="">
                                    <div className="">
                                        <SearchAndFilter
                                            searchValue={searchValue}
                                            onSearchChange={setSearchValue}
                                            filterFormats={filterFormats}
                                            onClearFormatFilters={() => setFilterFormats([])}
                                            onApplyFilters={(formats) => setFilterFormats(Array.from(formats))}
                                            showClearButton={true}
                                            disableClearButton={selectedImages.length === 0}
                                            onClearSelection={() => setSelectedImages([])}
                                        />
                                    </div>
                                </div>
                                <Library
                                    showHeader={false}
                                    onDelete={handleDelete}
                                    images={generatedImages}
                                    selectedImages={selectedImages}
                                    externalSearchValue={searchValue}
                                    externalFilterFormats={filterFormats}
                                    onExternalSearchChange={setSearchValue}
                                    handleConfirmDelete={handleConfirmDelete}
                                    onClearSelection={() => setSelectedImages([])}
                                    onExternalClearFormatFilters={() => setFilterFormats([])}
                                    showDeleteConfirmationModal={showDeleteConfirmationModal}
                                    onSelect={(id) => handleSelect(campaignFormat === 'carousel', id, 'ai')}
                                    onExternalApplyFilters={(formats) => setFilterFormats(Array.from(formats))}
                                    handleDeleteConfirmationCancel={() => setShowDeleteConfirmationModal(false)}
                                />
                            </div>
                        </TabsContent>
                    </Tabs>

                    {!isUploadMode && (
                        <div className="tw:px-6 tw:py-6">
                            <Button
                                className=""
                                variant="secondary"
                                onClick={handleGenerateClick}
                                disabled={loading || isGenerating || !hasSelectedImagesForActiveTab()}
                            >
                                {isGenerating && <LoaderCircle size={16} className="tw:text-white tw:animate-spin" />}
                                {!isGenerating && <AIIcon size={24} />}
                                Generate
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default AiImageToImage
